/**
 * CommandPalette — token-driven CSK command palette built on `cmdk`
 * and Radix Dialog (the dialog primitive that `cmdk.Dialog` already
 * wraps internally). Provides a typed, fuzzy-searchable navigator that
 * a host application can wire up to a ⌘K / Ctrl-K shortcut so users can
 * jump between any registered demo or theorem in a single keystroke.
 *
 * The component intentionally exposes `open` / `onOpenChange` on the
 * outside so the host stays in charge of the keyboard shortcut and the
 * persistence model. Items are typed: each carries an `id`, a `label`,
 * an optional `value` for ranking, an optional `keywords` array used by
 * `cmdk`'s fuzzy search, and an `onSelect` callback fired when the
 * user picks the row.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { Command } from "cmdk";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import React, { useCallback } from "react";
import { GRAY, PRIMARY } from "../../design-system/tokens.js";

export interface CommandPaletteItem {
  /** Stable identity used for React reconciliation. */
  readonly id: string;
  /** Visible label rendered inside the row. */
  readonly label: string;
  /** Optional secondary text rendered to the right of the label. */
  readonly hint?: React.ReactNode;
  /**
   * Fuzzy-search keywords. `cmdk` matches against the row's `value`
   * by default; supplying keywords broadens the match without
   * widening the visible label.
   */
  readonly keywords?: readonly string[];
  /** Optional group label (rendered as a section heading). */
  readonly group?: string;
  /** Callback invoked when the user picks the row. */
  readonly onSelect: () => void;
}

export interface CommandPaletteProps {
  /** Whether the palette is open. */
  readonly open: boolean;
  /** Open-state setter — wired to Radix Dialog's `onOpenChange`. */
  readonly onOpenChange: (open: boolean) => void;
  /** Items to render. Grouping is derived from each item's `group`. */
  readonly items: readonly CommandPaletteItem[];
  /** Visible placeholder shown inside the search input. */
  readonly placeholder?: string;
  /** Empty-state copy shown when no rows match the current query. */
  readonly emptyLabel?: string;
  /** Accessible label for the palette dialog. */
  readonly ariaLabel?: string;
  /** Optional title rendered above the search input. */
  readonly title?: React.ReactNode;
  /** Optional caption rendered above the empty/results region. */
  readonly description?: React.ReactNode;
}

const CONTAINER_STYLE: React.CSSProperties = {
  background: GRAY[50],
  border: `1px solid ${GRAY[300]}`,
  borderRadius: "0.75rem",
  boxShadow: "0 24px 48px rgba(15, 23, 42, 0.18)",
  color: GRAY[900],
  display: "grid",
  gap: "0.75rem",
  inset: "10vh auto auto 50%",
  maxHeight: "70vh",
  overflow: "hidden",
  padding: "1rem",
  position: "fixed",
  transform: "translateX(-50%)",
  width: "min(40rem, calc(100vw - 2rem))",
  zIndex: 70,
};

const INPUT_STYLE: React.CSSProperties = {
  background: "white",
  border: `1px solid ${GRAY[300]}`,
  borderRadius: "0.5rem",
  color: GRAY[900],
  fontSize: "0.95rem",
  outline: "none",
  padding: "0.6rem 0.75rem",
  width: "100%",
};

const LIST_STYLE: React.CSSProperties = {
  display: "grid",
  gap: "0.25rem",
  maxHeight: "50vh",
  overflowY: "auto",
};

const GROUP_HEADING_STYLE: React.CSSProperties = {
  color: GRAY[600],
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.05em",
  padding: "0.5rem 0.5rem 0.25rem",
  textTransform: "uppercase",
};

const ITEM_BASE_STYLE: React.CSSProperties = {
  borderRadius: "0.5rem",
  cursor: "pointer",
  display: "flex",
  fontSize: "0.875rem",
  gap: "0.75rem",
  justifyContent: "space-between",
  padding: "0.55rem 0.65rem",
};

const ITEM_LABEL_STYLE: React.CSSProperties = { color: GRAY[900], fontWeight: 500 };
const ITEM_HINT_STYLE: React.CSSProperties = { color: PRIMARY[700], fontSize: "0.75rem" };

const VISUALLY_HIDDEN_STYLE: React.CSSProperties = {
  border: 0,
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: 1,
};

const COMMAND_PALETTE_CSS = `
        .csk-command-palette-overlay { background: rgba(15,23,42,0.55); inset: 0; position: fixed; z-index: 60; }
        .csk-command-palette-content {
          inset: 10vh auto auto 50%;
          max-height: 70vh;
          position: fixed;
          transform: translateX(-50%);
          width: min(40rem, calc(100vw - 2rem));
          z-index: 70;
        }
        [cmdk-item][data-selected="true"] { background: ${PRIMARY[700]}; color: white !important; }
        [cmdk-item][data-selected="true"] * { color: white !important; }
      `;

interface PaletteItemProps {
  readonly item: CommandPaletteItem;
  readonly onAfterSelect: () => void;
}

const PaletteItem = React.memo(function PaletteItem({
  item,
  onAfterSelect,
}: PaletteItemProps): React.JSX.Element {
  const handle = useCallback((): void => {
    item.onSelect();
    onAfterSelect();
  }, [item, onAfterSelect]);
  const value = React.useMemo(
    () => `${item.label} ${(item.keywords ?? []).join(" ")}`,
    [item.keywords, item.label],
  );
  return (
    <Command.Item key={item.id} value={value} onSelect={handle} style={ITEM_BASE_STYLE}>
      <span style={ITEM_LABEL_STYLE}>{item.label}</span>
      {item.hint !== undefined && <span style={ITEM_HINT_STYLE}>{item.hint}</span>}
    </Command.Item>
  );
});

/**
 * Render a fuzzy-searchable command palette. The host owns the open
 * state so it can hook up `⌘K` / `Ctrl+K` itself.
 */
export const CommandPalette = React.memo(function CommandPalette({
  open,
  onOpenChange,
  items,
  placeholder = "Type a command or search…",
  emptyLabel = "No matches",
  ariaLabel = "CSK command palette",
  title,
  description,
}: CommandPaletteProps): React.JSX.Element {
  // Group items in render so callers do not have to pre-sort. Grouping
  // is preserved in input order; ungrouped items render under a
  // synthetic empty heading so they still appear.
  const groups = React.useMemo<
    readonly { heading: string; rows: readonly CommandPaletteItem[] }[]
  >(() => {
    const indexByGroup = new Map<string, CommandPaletteItem[]>();
    for (const item of items) {
      const heading = item.group ?? "";
      const bucket = indexByGroup.get(heading);
      if (bucket === undefined) {
        indexByGroup.set(heading, [item]);
      } else {
        bucket.push(item);
      }
    }
    return Array.from(indexByGroup.entries()).map(([heading, rows]) => ({ heading, rows }));
  }, [items]);

  const handleSelect = useCallback((): void => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label={ariaLabel}
      overlayClassName="csk-command-palette-overlay"
      contentClassName="csk-command-palette-content"
    >
      <style>{COMMAND_PALETTE_CSS}</style>
      <div style={CONTAINER_STYLE}>
        {title !== undefined ? (
          <DialogPrimitive.Title style={{ color: GRAY[900], fontSize: "1rem", fontWeight: 700 }}>
            {title}
          </DialogPrimitive.Title>
        ) : (
          <DialogPrimitive.Title style={VISUALLY_HIDDEN_STYLE}>{ariaLabel}</DialogPrimitive.Title>
        )}
        <Command.Input style={INPUT_STYLE} placeholder={placeholder} autoFocus />
        {description !== undefined ? (
          <DialogPrimitive.Description style={{ color: GRAY[600], fontSize: "0.75rem" }}>
            {description}
          </DialogPrimitive.Description>
        ) : (
          <DialogPrimitive.Description style={VISUALLY_HIDDEN_STYLE}>
            Search and activate commands.
          </DialogPrimitive.Description>
        )}
        <Command.List style={LIST_STYLE}>
          <Command.Empty style={{ color: GRAY[600], fontSize: "0.875rem", padding: "0.75rem" }}>
            {emptyLabel}
          </Command.Empty>
          {groups.map(({ heading, rows }) => (
            <Command.Group key={heading || "ungrouped"} heading={heading || undefined}>
              {heading !== "" && <div style={GROUP_HEADING_STYLE}>{heading}</div>}
              {rows.map((item) => (
                <PaletteItem key={item.id} item={item} onAfterSelect={handleSelect} />
              ))}
            </Command.Group>
          ))}
        </Command.List>
      </div>
    </Command.Dialog>
  );
});
